import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { highlight, languages } from 'prismjs/components/prism-core';
import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism.css';
import Editor from 'react-simple-code-editor';

import { FIELD_FRAME_CLASS, FOCUS_CLASS, TAG_CLASS, VARIABLE_COLOR_CLASS } from './templateStyles.ts';
import type { TemplateVariable } from './templateVariables.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import type { TemplateFormat } from '../OutputMapperEditor/types.ts';

// Jinja expressions, statements and comments — highlighted in the colours of the variable tags.
const JINJA_PATTERN = /(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}|\{#[\s\S]*?#\})/g;
// Private-use code points mark where a Jinja part was; they survive prism's tokenizing and HTML escaping untouched.
const PLACEHOLDER_START = String.fromCharCode(0xe000);
const PLACEHOLDER_END = String.fromCharCode(0xe001);
const PLACEHOLDER_PATTERN = new RegExp(`${PLACEHOLDER_START}(\\d+)${PLACEHOLDER_END}`, 'g');

const OpenIcon = withIcon(faArrowUpRightFromSquare);

// the "/" menu's width (w-80), as in the field configuration's instructions editor
const MENU_WIDTH = 320;

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Highlight a template: XML markup through prism, Jinja parts in the tag colours. Only colours are applied — no
 * padding or margin — so the highlighted layer stays aligned with the textarea the editor overlays it on.
 */
const KEY_HINT = (
    <kbd className="rounded border border-solid border-line-weak bg-surface-weak px-1 font-mono text-xs text-content-strong" />
);

const highlightTemplate = (code: string, format: TemplateFormat): string => {
    const jinjaParts: string[] = [];
    const withPlaceholders = code.replace(JINJA_PATTERN, (part) => {
        jinjaParts.push(part);
        return `${PLACEHOLDER_START}${jinjaParts.length - 1}${PLACEHOLDER_END}`;
    });
    const base = format === 'xml' ? highlight(withPlaceholders, languages.markup) : escapeHtml(withPlaceholders);
    return base.replace(PLACEHOLDER_PATTERN, (_, index: string) => {
        const part = jinjaParts[Number(index)];
        const colour = part.startsWith('{{') ? VARIABLE_COLOR_CLASS.variable : VARIABLE_COLOR_CLASS.loop;
        return `<span class="rounded ${colour}">${escapeHtml(part)}</span>`;
    });
};

const MIRRORED_STYLES = [
    'boxSizing',
    'width',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontSize',
    'lineHeight',
    'fontFamily',
    'letterSpacing',
    'wordSpacing',
    'tabSize',
    'whiteSpace',
    'wordBreak',
    'overflowWrap',
] as const;

// Pixel position of a caret inside a textarea, measured on an off-screen mirror with the same text layout.
const caretCoordinates = (textarea: HTMLTextAreaElement, position: number) => {
    const style = window.getComputedStyle(textarea);
    const mirror = document.createElement('div');
    MIRRORED_STYLES.forEach((property) => {
        mirror.style[property] = style[property];
    });
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.top = '0';
    mirror.style.left = '-9999px';
    mirror.textContent = textarea.value.slice(0, position);
    const marker = document.createElement('span');
    marker.textContent = String.fromCharCode(0x200b);
    mirror.appendChild(marker);
    document.body.appendChild(mirror);
    const coordinates = { top: marker.offsetTop + marker.offsetHeight, left: marker.offsetLeft };
    document.body.removeChild(mirror);
    return coordinates;
};

interface TemplateCodeEditorProps {
    value: string;
    onValueChange: (value: string) => void;
    variables: TemplateVariable[];
    format: TemplateFormat;
    placeholder?: string;
    /** Read-only while something else (the AI) writes the template; `overlay` is shown over the editor then. */
    locked?: boolean;
    overlay?: React.ReactNode;
    /** Open the target field's configuration (the Target fields tab). */
    onOpenTargetField?: (target: string) => void;
    /** Height of the editing area; the template uses half the page, the file name less. */
    height?: string;
    /** A one-line value (the file name): line breaks are dropped and Enter inserts nothing. */
    singleLine?: boolean;
    /** Small editor with the insert tags below it instead of the target-field column next to it. */
    compact?: boolean;
    /** The tags shown below a compact editor; the "/" menu always offers all `variables`. */
    paletteVariables?: TemplateVariable[];
}

interface SlashMenu {
    slashIndex: number;
    filter: string;
    top: number;
    left: number;
}

// The mark in front of a variable in the "/" menu, like the field configuration's DATA / FIELD marks.
const VariableMark = ({ kind }: { kind: TemplateVariable['kind'] }) => {
    const { t } = useTranslation('integrations');
    return (
        <span
            className={classnames(
                'flex-none rounded px-1 py-0.5 text-[0.625em] font-medium uppercase',
                VARIABLE_COLOR_CLASS[kind]
            )}
        >
            {kind === 'loop'
                ? t('outputMapper.template.loopMark', 'List')
                : kind === 'builtin'
                  ? t('outputMapper.template.builtinMark', 'Built-in')
                  : t('outputMapper.template.variableMark', 'Field')}
        </span>
    );
};

/**
 * Code editor for Output Studio templates, framed like the field configuration's instructions box. Typing `/` opens
 * a menu of the mapping's variables; picking one replaces the `/filter` with `{{ variable }}`, or with a
 * `{% for %}` skeleton for an array. The variables are also listed next to the editor for click-to-insert.
 */
const TemplateCodeEditor = ({
    value,
    onValueChange,
    variables,
    format,
    placeholder,
    locked = false,
    overlay,
    onOpenTargetField,
    height = '50vh',
    singleLine = false,
    compact = false,
    paletteVariables,
}: TemplateCodeEditorProps) => {
    const { t } = useTranslation('integrations');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [menu, setMenu] = useState<SlashMenu | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const textarea = () => wrapperRef.current?.querySelector('textarea') ?? null;

    // the editor highlights on every render (caret moves, menu updates); only a changed text needs a new pass
    const highlighted = useMemo(() => highlightTemplate(value, format), [value, format]);
    const highlightCode = () => highlighted;

    // values first, then loops, so the menu reads in sections like the field configuration's
    const filtered = useMemo(() => {
        if (!menu) return [];
        const lower = menu.filter.toLowerCase();
        const matches = lower
            ? variables.filter((variable) => variable.value.toLowerCase().includes(lower))
            : variables;
        return (['variable', 'loop', 'builtin'] as const).flatMap((kind) => matches.filter((v) => v.kind === kind));
    }, [menu, variables]);

    const replaceRange = (start: number, end: number, text: string) => {
        onValueChange(value.slice(0, start) + text + value.slice(end));
        requestAnimationFrame(() => {
            const element = textarea();
            if (!element) return;
            element.focus();
            element.selectionStart = element.selectionEnd = start + text.length;
        });
    };

    const closeMenu = () => {
        setMenu(null);
        setSelectedIndex(0);
    };

    const pick = (variable: TemplateVariable) => {
        if (!menu) return;
        const caret = textarea()?.selectionStart ?? menu.slashIndex + 1 + menu.filter.length;
        replaceRange(menu.slashIndex, caret, variable.insert);
        closeMenu();
    };

    const insertVariable = (variable: TemplateVariable) => {
        if (locked) return;
        const element = textarea();
        const start = element?.selectionStart ?? value.length;
        replaceRange(start, element?.selectionEnd ?? start, variable.insert);
    };

    const openMenuAtCaret = () => {
        const element = textarea();
        if (!element || variables.length === 0) return;
        const slashIndex = element.selectionStart - 1;
        if (element.value[slashIndex] !== '/') return;
        const caret = caretCoordinates(element, slashIndex);
        const maxLeft = (wrapperRef.current?.clientWidth ?? MENU_WIDTH) - MENU_WIDTH;
        setMenu({
            slashIndex,
            filter: '',
            top: element.offsetTop + caret.top + 2,
            // at the caret, moved left only as far as needed to stay inside the editor
            left: Math.max(0, Math.min(caret.left, maxLeft)),
        });
        setSelectedIndex(0);
    };

    // Keep the menu's filter in sync with what was typed after the slash; close it once the slash is gone,
    // the caret left the word, or a space / line break ends it.
    const syncMenu = (nextValue: string) => {
        if (!menu) return;
        requestAnimationFrame(() => {
            const caret = textarea()?.selectionStart ?? 0;
            const filter = nextValue.slice(menu.slashIndex + 1, caret);
            if (nextValue[menu.slashIndex] !== '/' || caret <= menu.slashIndex || /\s/.test(filter)) {
                closeMenu();
                return;
            }
            setMenu((current) => (current ? { ...current, filter } : current));
            setSelectedIndex(0);
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === '/' && !menu) {
            requestAnimationFrame(openMenuAtCaret);
            return;
        }
        if (!menu) {
            if (singleLine && event.key === 'Enter') event.preventDefault();
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedIndex((index) => Math.min(index + 1, filtered.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedIndex((index) => Math.max(index - 1, 0));
        } else if ((event.key === 'Enter' || event.key === 'Tab') && filtered.length > 0) {
            event.preventDefault();
            pick(filtered[selectedIndex]);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            closeMenu();
        }
    };

    const sectionLabel = (kind: TemplateVariable['kind']) =>
        kind === 'loop'
            ? t('outputMapper.template.menuLoops', 'Lists (inserted as a loop)')
            : kind === 'builtin'
              ? t('outputMapper.template.menuBuiltins', 'Built-in values')
              : t('outputMapper.template.menuValues', 'Target fields');

    // the editor with its "/" menu; the full layout frames it next to the target fields, the compact one alone
    const editorWithMenu = (
        <div ref={wrapperRef} className="relative">
            <Editor
                className="text-xs font-mono"
                // fills the frame, so a click anywhere in it lands in the textarea
                style={{ minHeight: `calc(${height} - 2px)` }}
                value={value}
                readOnly={locked}
                onValueChange={(next: string) => {
                    const cleaned = singleLine ? next.replace(/[\r\n]+/g, '') : next;
                    onValueChange(cleaned);
                    syncMenu(cleaned);
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(closeMenu, 150)}
                highlight={highlightCode}
                padding={compact ? 8 : 12}
                placeholder={placeholder}
            />
            {menu && filtered.length > 0 && (
                <div
                    className="absolute z-50 max-h-72 w-80 overflow-auto rounded-lg border border-solid border-line-weak bg-surface-weak py-1 shadow-lg"
                    style={{ top: menu.top, left: menu.left }}
                >
                    {filtered.map((variable, index) => (
                        <React.Fragment key={variable.value}>
                            {(index === 0 || filtered[index - 1].kind !== variable.kind) && (
                                <div className="px-3 pb-1 pt-2 text-xs font-medium text-content-medium">
                                    {sectionLabel(variable.kind)}
                                </div>
                            )}
                            <div
                                className={classnames(
                                    'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                                    index === selectedIndex ? 'bg-surface-strong' : 'hover:bg-surface-strong'
                                )}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    pick(variable);
                                }}
                                onMouseEnter={() => setSelectedIndex(index)}
                            >
                                <VariableMark kind={variable.kind} />
                                <div className="flex min-w-0 flex-col">
                                    <div className="truncate font-medium">{variable.value}</div>
                                    <div className="truncate font-mono text-xs text-content-weak">
                                        {variable.insert.split('\n')[0]}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );

    if (compact) {
        return (
            <div className="flex flex-col gap-1.5">
                <div className={FIELD_FRAME_CLASS}>{editorWithMenu}</div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-content-medium">
                    <span>
                        <Trans
                            t={t}
                            i18nKey="outputMapper.template.compactHowTo"
                            defaults="Insert, or type <key>/</key> for all target fields:"
                            components={{
                                key: KEY_HINT,
                            }}
                        />
                    </span>
                    {(paletteVariables ?? variables).map((variable) => (
                        <button
                            key={variable.value}
                            type="button"
                            title={variable.insert}
                            disabled={locked}
                            className={classnames(
                                TAG_CLASS,
                                VARIABLE_COLOR_CLASS[variable.kind],
                                'cursor-pointer border-0 pr-1.5 transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50',
                                FOCUS_CLASS
                            )}
                            // onMouseDown (not onClick) so the textarea keeps its caret/selection.
                            onMouseDown={(event) => {
                                event.preventDefault();
                                insertVariable(variable);
                            }}
                        >
                            {variable.value}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col gap-1.5">
            {/* no overflow-hidden on the frame: it would clip the "/" menu, so the parts round their own corners */}
            <div className={classnames(FIELD_FRAME_CLASS, 'flex-1 flex-row')} style={{ minHeight: height }}>
                <div className="relative min-w-0 flex-1">
                    <div className="absolute inset-0 overflow-auto rounded-l">{editorWithMenu}</div>
                    {locked && overlay && (
                        <div className="absolute inset-0 z-40 flex items-center justify-center rounded-l bg-surface-weak/85 backdrop-blur-sm">
                            {overlay}
                        </div>
                    )}
                </div>

                <div className="flex w-72 flex-none flex-col gap-2 rounded-r border-0 border-l border-solid border-line-weak bg-surface-medium px-3 py-2">
                    <span className="text-xs font-medium text-content-medium">
                        {t('outputMapper.template.targetFieldsTitle', 'Target fields')}
                    </span>
                    {variables.length === 0 ? (
                        <span className="text-xs text-content-weak">
                            {t('outputMapper.template.noVariables', 'The mapping has no targets yet.')}
                        </span>
                    ) : (
                        <div className="flex min-h-0 flex-1 flex-col items-start gap-1 overflow-auto">
                            {variables.map((variable) => (
                                <div key={variable.value} className="flex w-full min-w-0 items-center gap-1">
                                    <button
                                        type="button"
                                        title={variable.insert}
                                        disabled={locked}
                                        className={classnames(
                                            TAG_CLASS,
                                            VARIABLE_COLOR_CLASS[variable.kind],
                                            'min-w-0 cursor-pointer border-0 pr-1.5 text-left transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50',
                                            FOCUS_CLASS
                                        )}
                                        // onMouseDown (not onClick) so the textarea keeps its caret/selection.
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            insertVariable(variable);
                                        }}
                                    >
                                        <span className="truncate">{variable.value}</span>
                                    </button>
                                    {onOpenTargetField && variable.kind !== 'builtin' && (
                                        <button
                                            type="button"
                                            disabled={locked}
                                            aria-label={t('outputMapper.template.openTargetField', {
                                                defaultValue: 'Configure target field {{name}}',
                                                name: variable.value,
                                            })}
                                            title={t('outputMapper.template.openTargetField', {
                                                defaultValue: 'Configure target field {{name}}',
                                                name: variable.value,
                                            })}
                                            onClick={() => onOpenTargetField(variable.value)}
                                            className={classnames(
                                                'flex flex-none cursor-pointer items-center rounded border-0 bg-transparent p-1 text-content-weak transition-colors hover:text-content-strong disabled:cursor-not-allowed disabled:opacity-50',
                                                FOCUS_CLASS
                                            )}
                                        >
                                            <OpenIcon className="text-[0.625rem]" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <span className="text-xs leading-6 text-content-medium">
                <Trans
                    t={t}
                    i18nKey="outputMapper.template.howTo"
                    defaults="Type <key>/</key> in the template or click a target field to insert it at the cursor. Lists insert a loop; missing values render empty."
                    components={{
                        key: KEY_HINT,
                    }}
                />
            </span>
        </div>
    );
};

export default TemplateCodeEditor;
